import { z } from 'zod'

/**
 * Why the microphone is on or off right now. Supplied by the host (the room
 * adapter, the AV check, or the device picker). MicCheck never guesses intent
 * from a disabled track; it is told.
 */
export type MicContext =
  | 'active'
  | 'user_muted'
  // observers, and slides that isolate participants
  | 'not_required'
  | 'reconnecting'
  | 'repairing'

/** A confirmed microphone fault. */
export type MicFailure =
  | 'no_track'
  | 'track_stopped'
  | 'digital_silence'
  // A live track whose sound never rises above a faint hiss: it passes the
  // digital-zero test but nobody can hear it.
  | 'near_silence'

/** Whether the speech detector loaded and can report speech. */
export type SpeechDetection = 'available' | 'unavailable'

/**
 * What the microphone is doing. `ready` means a live, enabled track has produced
 * non-zero audio; it does not mean anyone else can hear it or that transcription
 * works.
 */
export type MicReport =
  | { state: 'inactive'; reason: 'user_muted' | 'not_required' }
  | {
      state: 'checking'
      reason: 'starting' | 'changing_device' | 'reconnecting' | 'repairing'
    }
  | {
      state: 'ready'
      hasHeardSpeech: boolean
      speechDetection: SpeechDetection
    }
  | { state: 'failed'; cause: MicFailure }

/** Which layer owns a problem. */
export type ProblemLayer = 'microphone' | 'delivery' | 'transcript'

/** Everything the UI can be asked to explain. */
export type ProblemCause = MicFailure | 'unreachable' | 'no_transcript'

export type Problem = { cause: ProblemCause; since: number }

/** How a resolved problem was recovered, for telemetry. */
export type ResolvedBy =
  | 'mic_ready'
  | 'server_speech'
  | 'heartbeat_speech'
  | 'caption'

/** The heartbeat observations the controller can derive. */
export type HeartbeatObservationKind =
  | 'no_heartbeat'
  | 'not_listening'
  | 'no_packets'
  | 'decode_errors'
  | 'no_audible_frames'
  // Audible frames reach the worker but its speech detector starts no speech.
  | 'no_speech_starts'

/**
 * The Secretary status message. Sent by the worker every intervalMS over the
 * LiveKit data channel, message type 16, unreliable transport. A fresh message
 * proves the status publisher is running; a row for the current microphone means
 * its audio loop is active; advancing packet counters mean audio is arriving.
 * Only actual captions prove transcription output.
 */
export const secretaryStatusTrackSchema = z.object({
  identity: z.string(),
  // LiveKit microphone publication SID.
  trackId: z.string(),
  packets: z.number(),
  decodeErrors: z.number(),
  frames: z.number(),
  // Absent means not measured, not zero.
  framesAboveGate: z.number().optional(),
  speechStarts: z.number().optional(),
})

export const secretaryStatusSchema = z.object({
  type: z.literal(16),
  // Unique to this worker run.
  runId: z.string(),
  seq: z.number(),
  intervalMS: z.number(),
  engine: z.string(),
  tracks: z.array(secretaryStatusTrackSchema),
})

export type SecretaryStatusTrack = z.infer<typeof secretaryStatusTrackSchema>
export type SecretaryStatus = z.infer<typeof secretaryStatusSchema>

/** The calibration the controller and MicCheck read. Mirrors settings/soundcheck. */
export type SoundcheckConfig = {
  notificationsEnabled: boolean
  tickMS: number
  micGraceMS: number
  publishGraceMS: number
  silenceConfirmDelayMS: number
  silencePollIntervalMS: number
  nearSilenceRMS: number
  nearSilenceMS: number
  avCheckNearSilenceMS: number
  reachabilityDelayMS: number
  reachabilityWindowMS: number
  reachabilityMinSpeechMS: number
  transcriptMinSpeechMS: number
  noTranscriptAfterMS: number
  unmutedNoTranscriptAfterMS: number
  heartbeatTimeoutMS: number
  notListeningConfirmMS: number
  serverSpeechNoCaptionMS: number
}

/** Which host is running a MicCheck, for the soundcheck_mic_result telemetry. */
export type MicCheckHost = 'avcheck' | 'demo' | 'dialog' | 'meeting'
